/**
 * Adapter — the `calibres[]` half of an extractor `ExtractionResult`.
 *
 * The sibling `extractionToEntries` adapter deliberately drops calibre data: a
 * watch's `movement` STRING is all the import pipeline needs, and the calibre
 * step resolves that against `custom_calibres` (richer than any scrape). But
 * when the step comes up empty — a brand whose calibres aren't in the DB yet —
 * the extractor's own calibre pages are the best source we have, and until now
 * they were thrown away.
 *
 * This module turns them into a pre-filled create form: match a pending calibre
 * row to a scraped calibre by name, map the scrape onto the calibre editor's
 * fields, and let the admin review before saving. Nothing here writes anything.
 *
 * TYPE NOTE — the top-level `calibres` array is validated STRUCTURALLY rather
 * than trusted off the type: `extractionResultCalibres` takes `unknown`, because
 * the same entry point also receives hand-rolled JSON uploads and results from
 * extractor versions that predate the field.
 */
import type { ExtractedCalibre, ExtractionResult } from '@watchkeeper/shared-types';
import { normalizeCalibreKey } from '../../../services/calibreResolution';
import type { CalibreData } from '../../../services/firebase';

/**
 * Seed values for {@link CustomCalibreModal}'s create form. Every field is
 * already in the form's own shape — numeric fields are STRINGS, because
 * `FormState` stores them as raw input text and only parses at save time. All
 * mapping and unit parsing happens here so the modal stays a dumb consumer.
 */
export interface CalibreCreateSeed {
  name?: string;
  movementType?: string;
  description?: string;
  frequencyVph?: string;
  jewels?: string;
  powerReserveHours?: string;
  diameterMm?: string;
  heightMm?: string;
  lignes?: string;
  baseMovement?: string;
  isChronograph?: boolean;
  date?: string[];
  hands?: string[];
  chronograph?: string[];
  additionals?: string[];
  /** Provenance URL of the scraped calibre page; seeds the form's Source URL. */
  sourceUrl?: string;
}

/**
 * Comparison key for matching a pending calibre row to a scraped calibre.
 *
 * A thin alias of the shared {@link normalizeCalibreKey}, kept as a named export
 * because the extractor call sites read better with it. The NFC pass this
 * wrapper used to add itself now lives in normalizeCalibreKey (mirrored in
 * functions/src/calibreKey.ts), so every calibre comparison in the admin gets
 * it — not just the extractor's.
 */
export function extractorCalibreKey(value: string): string {
  return normalizeCalibreKey(value);
}

/** Structural guard — the extractor's calibre entries are all-optional bar the URL. */
function isExtractedCalibre(x: unknown): x is ExtractedCalibre {
  if (typeof x !== 'object' || x === null || Array.isArray(x)) return false;
  return typeof (x as Record<string, unknown>).sourceUrl === 'string';
}

/**
 * Pulls the calibre payload off an extractor result. Returns `[]` for any
 * payload without a usable `calibres` array — a hand-rolled entry upload, an
 * older extractor version, or a run that scraped no calibre pages — so callers
 * can hold this unconditionally.
 */
export function extractionResultCalibres(result: unknown): ExtractedCalibre[] {
  const calibres = (result as Partial<ExtractionResult> | null | undefined)
    ?.calibres;
  if (!Array.isArray(calibres)) return [];
  return calibres.filter(isExtractedCalibre);
}

/**
 * Indexes scraped calibres by comparison key. Entries with no `calibre` name
 * are skipped — there is nothing to match them on. First writer wins, so a
 * duplicate name doesn't silently swap which page's specs the admin sees.
 */
export function buildExtractorCalibreIndex(
  calibres: ExtractedCalibre[],
): Map<string, ExtractedCalibre> {
  const idx = new Map<string, ExtractedCalibre>();
  for (const c of calibres) {
    const name = c.calibre?.trim();
    if (!name) continue;
    const key = extractorCalibreKey(name);
    if (!idx.has(key)) idx.set(key, c);
  }
  return idx;
}

/**
 * First number in a free-text spec string. Thousands separators are stripped
 * first so "18,000 vph" reads as 18000 rather than 18.
 */
function firstNumber(raw?: string): number | undefined {
  if (!raw) return undefined;
  const m = raw.replace(/(\d),(\d{3})\b/g, '$1$2').match(/-?\d+(?:[.,]\d+)?/);
  if (!m) return undefined;
  const n = parseFloat(m[0].replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/** Formats a parsed number back to the form's string field, dropping `undefined`. */
function num(n?: number): string | undefined {
  return n === undefined ? undefined : String(n);
}

/**
 * Beats-per-hour from the extractor's free-text frequency.
 *
 * Sources label it three ways and often two at once ("18,000 vph (2.5 Hz)"), so
 * an explicit vph/A-h/bph unit wins outright; a bare Hz value is converted
 * (1 Hz = 2 beats/s = 7200 vph); otherwise the first number is only trusted if
 * it's already at vph scale, so a stray "2.5" never lands in the field.
 */
function parseFrequencyVph(raw?: string): number | undefined {
  if (!raw) return undefined;
  const text = raw.replace(/(\d),(\d{3})\b/g, '$1$2');
  const unit = text.match(/(\d+(?:\.\d+)?)\s*(?:vph|bph|a\s*\/\s*h)/i);
  if (unit) return Math.round(parseFloat(unit[1]));
  const hz = text.match(/(\d+(?:[.,]\d+)?)\s*hz/i);
  if (hz) return Math.round(parseFloat(hz[1].replace(',', '.')) * 7200);
  const bare = firstNumber(text);
  return bare !== undefined && bare >= 3600 ? Math.round(bare) : undefined;
}

/** First millimetre measurement in a string ("14 ¼ lines (33.5 mm)" → 33.5). */
function parseMm(raw?: string): number | undefined {
  const m = raw?.match(/(\d+(?:[.,]\d+)?)\s*mm/i);
  if (!m) return undefined;
  const n = parseFloat(m[1].replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/** The ligne portion of a diameter string, when the source quotes one. */
function parseLignes(raw?: string): string | undefined {
  const m = raw?.match(/^([^()]*?(?:lignes?|lines?|'''))/i);
  return m ? m[1].trim() || undefined : undefined;
}

/**
 * Movement height, which no source puts in a dedicated field — it arrives as a
 * labelled row inside the lossless `additional` catch-all ("Height 4.9 mm").
 */
function parseHeightMm(additional?: Record<string, string[]>): number | undefined {
  if (!additional) return undefined;
  for (const values of Object.values(additional)) {
    if (!Array.isArray(values)) continue;
    for (const v of values) {
      const m = typeof v === 'string' && v.match(/(?:height|thickness)\D*(\d+(?:[.,]\d+)?)\s*mm/i);
      if (m) {
        const n = parseFloat(m[1].replace(',', '.'));
        if (Number.isFinite(n)) return n;
      }
    }
  }
  return undefined;
}

/** `additional` keys that hold a complication list, matched case-insensitively. */
const FUNCTION_KEYS = new Set(['functions', 'function', 'complications', 'complication']);

/**
 * Complication list for the form's free-text "Additionals" tags. Prefers the
 * already-split `additional.Functions` rows; falls back to comma-splitting the
 * single-line `display` string that carries the same content on sources which
 * don't break it out.
 */
function parseComplications(c: ExtractedCalibre): string[] {
  if (c.additional) {
    for (const [key, values] of Object.entries(c.additional)) {
      if (!FUNCTION_KEYS.has(key.trim().toLowerCase())) continue;
      if (!Array.isArray(values)) continue;
      const cleaned = values.map((v) => String(v).trim()).filter(Boolean);
      if (cleaned.length > 0) return cleaned;
    }
  }
  return (c.display ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Drops undefined/empty values so the seed only carries fields we actually parsed. */
function stripSeed(seed: CalibreCreateSeed): CalibreCreateSeed {
  return Object.fromEntries(
    Object.entries(seed).filter(([, v]) => {
      if (v === undefined) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'string') return v !== '';
      return true;
    }),
  ) as CalibreCreateSeed;
}

/**
 * The seed's spec fields as a `CalibreData` payload, for the bulk path that
 * writes without ever rendering the form.
 *
 * Deliberately routed through {@link CalibreCreateSeed} rather than mapping
 * `ExtractedCalibre` → `CalibreData` directly. The seed is the form's shape, so
 * going through it guarantees the bulk create writes EXACTLY what the per-row
 * "From Extractor" pill would have shown the admin — one parsing implementation,
 * not two that drift. The string→number round trip is the price of that, and
 * it's a cheap one: every value here was parsed from a number in the first
 * place, so `Number()` returns it unchanged.
 *
 * `name`, `description` and `sourceUrl` are absent by design — those are
 * top-level `saveCustomCalibre` arguments, not members of the nested `data` map,
 * and the caller passes them separately.
 */
export function calibreSeedToData(seed: CalibreCreateSeed): CalibreData {
  const data: CalibreData = {};
  const numeric = (raw: string | undefined): number | undefined => {
    if (raw === undefined || raw === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };
  const setNum = (key: 'frequencyVph' | 'jewels' | 'powerReserveHours' | 'diameterMm' | 'heightMm') => {
    const n = numeric(seed[key]);
    if (n !== undefined) data[key] = n;
  };
  if (seed.movementType) data.movementType = seed.movementType;
  if (seed.baseMovement) data.baseMovement = seed.baseMovement;
  if (seed.lignes) data.lignes = seed.lignes;
  setNum('frequencyVph');
  setNum('jewels');
  setNum('powerReserveHours');
  setNum('diameterMm');
  setNum('heightMm');
  // Only ever set true — `isChronograph: false` is indistinguishable from "the
  // scrape didn't say", and writing the negative would claim knowledge we lack.
  if (seed.isChronograph) data.isChronograph = true;
  if (seed.date?.length) data.date = seed.date;
  if (seed.hands?.length) data.hands = seed.hands;
  if (seed.chronograph?.length) data.chronograph = seed.chronograph;
  if (seed.additionals?.length) data.additionals = seed.additionals;
  return data;
}

/**
 * Maps a scraped calibre onto the calibre editor's create form.
 *
 * `name` comes from the CALLER (the pending row), not from the scrape: the row
 * name is what the import will look the calibre up by, so seeding anything else
 * risks creating a doc the very import that prompted it can't resolve. The
 * admin can still rename in the form — that's a deliberate choice, not a
 * silent mismatch.
 *
 * Everything the extractor didn't provide is simply absent; the form field
 * stays empty rather than being filled with a guess.
 */
export function extractedCalibreToSeed(
  name: string,
  c: ExtractedCalibre,
): CalibreCreateSeed {
  return stripSeed({
    name: name.trim(),
    movementType: c.movementType?.trim(),
    baseMovement: c.base?.trim(),
    frequencyVph: num(parseFrequencyVph(c.frequency)),
    jewels: num(firstNumber(c.jewels)),
    // "24/42 h" (remontoir / follow-on) yields 24 — the first stated figure.
    // Ambiguous by nature; the admin sees it in an editable field.
    powerReserveHours: num(firstNumber(c.powerReserve)),
    diameterMm: num(parseMm(c.diameter)),
    heightMm: num(parseHeightMm(c.additional)),
    lignes: parseLignes(c.diameter),
    isChronograph: c.isChronograph || undefined,
    date: c.date?.map((d) => d.trim()).filter(Boolean),
    hands: c.hands?.map((h) => h.trim()).filter(Boolean),
    chronograph: c.chronograph?.trim() ? [c.chronograph.trim()] : undefined,
    additionals: parseComplications(c),
    sourceUrl: c.sourceUrl?.trim(),
  });
}
